import MDinput from '@/components/MDinput';
import Select from '@/components/DSE/Select';
import AddressInput from '@/components/DSE/AddressInput';
import { validatorEmail, validatorRequire, validatorRequireNumber } from '@/utils/validators';
import ProductList from './products/components/Products/Products';
import CrudContactPerson from '../components/CrudContactPerson';
import CrudLinkDomainProductGroup from './productgroups/CrudLinkDomainProductGroup';
import InputNumber from '@/components/DSE/InputNumber';
import CrudStoreAttributes from './storeattributes/CrudStoreAttributes';
import CrudStoreTypes from './storetypes/CrudStoreTypes';
import CrudPriceListItems from './pricelistitems/index';
import CrudCultures from './cultures/CrudCultures';
import CrudDeliveryPrices from './deliveryprices/CrudDeliveryPrices';
import CrudGeneralLedgerAccounts from './generalledgeraccounts/CrudGeneralLedgerAccounts';
import Switch from '@/components/DSE/Switch';
import { DomainTypeOption } from '@/utils/constants';

export function getFormDefinition(vue) {
  return {
    type: 'tab',
    saveButtons: [true, false, false, false, false, false, false, false, false, false, false, false], // Page, tab 1, tab 2, ...
    showFormLabels: [true, true, false, false, false, false, false, false, false, false, false], // Tab 1, tab 2, ....
    labels: [
      'domain.tabGeneral',
      'domain.tabSettings',
      'domain.tabContactPersons',
      'domain.tabProducts',
      'productGroup.productGroups',
      'domain.storeAttributes',
      'domain.storeTypes',
      'domain.cultures',
      'priceListItem.priceListItems',
      'domain.deliveryPrices',
      'common.glAccountNumbers'
    ],
    headerTemplate: (str, form) => `
    <h3>
    ${form.name || ''}
    </h3>
    `,
    groups: [
      {
        // Common
        name: {
          type: MDinput,
          defaultValue: '',
          props: {
            maxlength: 64,
            required: true
          },
          rules: [
            {
              validator: validatorRequire
            }
          ]
        },
        copyLinksFromDomainID: {
          type: Select,
          props: {
            caption: 'domain.copyDataFromDomain',
            placeholder: vue.$t('common.domain'),
            defaultValue: null,
            valueField: 'id',
            labelField: 'name',
            options: vue.$store.getters.domains
          },
          dynamicProps: form => {
            return {
              visible: !form.id
            };
          }
        },
        description: {
          type: MDinput,
          defaultValue: '',
          props: {
            maxlength: 255
          }
        },
        domainType: {
          type: Select,
          defaultValue: DomainTypeOption.Retailer,
          props: {
            caption: 'common.type',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('domainType'),
            required: true
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        companyID: {
          type: Select,
          defaultValue: '',
          props: {
            caption: 'domain.company',
            required: true,
            valueField: 'id',
            labelField: 'name',
            loading: vue.$store.getters['company/isLoading'],
            options: vue.$store.getters['company/userLinkedCompanies']
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        companyAddress: {
          type: AddressInput,
          defaultValue: null,
          props: {
            countries: vue.$store.getters['country/loadedItems'],
            countriesLoading: vue.$store.getters['country/isLoading']
          }
        },
        mailingAddress: {
          type: AddressInput,
          defaultValue: null,
          props: {
            countries: vue.$store.getters['country/loadedItems'],
            countriesLoading: vue.$store.getters['country/isLoading']
          }
        },
        companyEmail: {
          type: MDinput,
          props: {
            type: 'email',
            maxlength: 64
          },
          rules: [{ validator: validatorEmail }]
        },
        phone: {
          type: MDinput,
          props: {
            type: 'tel'
          }
        }
      },
      {
        // settings
        paymentTermDays: {
          type: InputNumber,
          defaultValue: null,
          props: {
            caption: 'domain.paymentTermDays'
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        paymentDiscountPercentage: {
          type: InputNumber,
          defaultValue: null,
          props: {
            caption: 'domain.paymentDiscountPercentage',
            min: 0,
            max: 100,
            precision: 1,
            step: 0.5
          }
        },
        invoicePer: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'domain.invoicePer',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('invoicePer'),
            required: true
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        showOrdersInvoicePer: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'domain.showOrdersInvoicePer',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('invoicePer'),
            required: true
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        export: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'domain.export',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('export'),
            required: true
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        stripCorrection: {
          type: InputNumber,
          defaultValue: 0,
          props: {
            caption: 'domain.stripCorrection',
            precision: 2,
            min: -0.99,
            max: 0.99,
            step: 0.01
          }
        },
        distributionCenterID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'domain.distributionCenter',
            valueField: 'id',
            labelField: 'name',
            loading: vue.$store.getters['stockLocation/isLoading'],
            options: vue.$store.getters['stockLocation/loadedItems']
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        lastStockWorkshopID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'domain.lastStockWorkshop',
            valueField: 'id',
            labelField: 'name',
            loading: vue.$store.getters['workshop/isLoading'],
            options: vue.$store.getters['workshop/loadedItems']
          }
        },
        nonDiverseLiningIDs: {
          type: Select,
          defaultValue: [],
          props: {
            caption: 'fabric.nonDiverseLinings',
            valueField: 'id',
            labelField: 'domainProductCode',
            multiple: true,
            loading: vue.$store.getters['fabric/isLoading'],
            options: vue.$store.getters['fabric/loadedItems']
          }
        },
        orderNumberPrefix: {
          type: MDinput,
          defaultValue: '',
          props: {
            minlength: 2,
            maxlength: 2,
            caption: 'domain.orderNumberPrefix',
            required: true
          },
          rules: [{ validator: validatorRequire }]
        },
        rfidEnabledYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.rfidEnabledYN',
            activeText: 'common.on',
            inactiveText: 'common.off'
          }
        },
        rfidPrefix: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            min: 1,
            max: 99,
            step: 1,
            caption: 'domain.rfidPrefix'
          }
        },
        barcodePrefix: {
          type: InputNumber,
          defaultValue: 0,
          props: {
            min: 0,
            max: 99,
            step: 1,
            caption: 'domain.barcodePrefix',
            required: true
          },
          rules: [{ validator: validatorRequire }]
        },
        shareHalfPanelsYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.shareHalfPanels',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        quarterPanelsYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.quarterPanels',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        defaultDeliverAtHomeYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.defaultDeliverAtHomeYN',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        createFabricsYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.createFabrics',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        standardDeliveryPeriodInDays: {
          type: InputNumber,
          defaultValue: null,
          props: {
            caption: 'domain.standardDeliveryPeriod'
          }
        },
        transportCompanyID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'transport.transportCompany',
            required: false,
            valueField: 'id',
            labelField: 'name',
            loading: vue.$store.getters['transportCompany/isLoading'],
            options: vue.$store.getters['transportCompany/loadedItems']
          }
        },
        transportOrderMultipleWorkshopsYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.transportOrderMultipleWorkshops',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        ticketLanguageID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'domain.ticketLanguage',
            required: false,
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['language/isLoading'],
            options: vue.$store.getters['language/loadedItems']
          }
        },
        familyOrderPackingPriceListItemID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'domain.familyOrderPacking',
            required: false,
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['priceListItem/isLoading'],
            options: vue.$store.getters['priceListItem/loadedItems']
          }
        },
        houseNumberValidationDisabledYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.houseNumberValidationDisabled',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        autoProcessFabricOrdersYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.autoProcessFabricOrders',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        keepCalculatedValuesOnReturnOrderYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.keepCalculatedValuesOnReturnOrder',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        allowDuplicateStoreCodesYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'domain.allowDuplicateStoreCodes',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        calculateOrderLinesForInteriorDecoratorYN: {
          type: Switch,
          group: 'Woninginrichter',
          props: {
            caption: 'domain.calculateOrderLinesForInteriorDecorator',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        inactiveYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'common.inactive',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        }
      },
      {
        // contactPersons
        contactPersons: {
          type: CrudContactPerson,
          defaultValue: [],
          props: {
            caption: 'common.emptyString',
            openInFullscreen: false,
            parentIdFilterField: 'domainID',
            permissionCode: 'Domain'
          }
        }
      },
      {
        // products
        products: {
          type: ProductList,
          props: {
            caption: 'common.emptyString'
          }
        }
      },
      {
        // productGroups
        productGroups: {
          type: CrudLinkDomainProductGroup,
          defaultValue: [],
          props: {
            tabbed: true,
            caption: 'common.emptyString',
            openInFullscreen: false,
            parentIdFilterField: 'domainID',
            permissionCode: 'Domain'
          }
        }
      },
      {
        // storeAttributes
        storeAttributes: {
          type: CrudStoreAttributes,
          defaultValue: [],
          props: {
            caption: 'common.emptyString',
            openInFullscreen: false,
            parentIdFilterField: 'domainID',
            permissionCode: 'Domain'
          }
        }
      },
      {
        // storeTypes
        storeTypes: {
          type: CrudStoreTypes,
          defaultValue: [],
          props: {
            caption: 'common.emptyString',
            openInFullscreen: false,
            parentIdFilterField: 'domainID',
            permissionCode: 'Domain'
          }
        }
      },
      {
        // Cultures
        Cultures: {
          type: CrudCultures,
          defaultValue: [],
          props: {
            caption: 'common.emptyString',
            openInFullscreen: true,
            parentIdFilterField: 'domainID',
            permissionCode: 'Domain'
          }
        }
      },
      {
        // PriceListItems
        PriceListItems: {
          type: CrudPriceListItems,
          defaultValue: [],
          props: {
            caption: 'common.emptyString',
            openInFullscreen: true,
            parentIdFilterField: 'domainID',
            permissionCode: 'Domain'
          }
        }
      },
      {
        // DeliveryPrices
        DeliveryPrices: {
          type: CrudDeliveryPrices,
          defaultValue: [],
          props: {
            caption: 'common.emptyString',
            openInFullscreen: true,
            parentIdFilterField: 'domainID',
            permissionCode: 'Domain'
          }
        }
      },
      {
        // GeneralLedgerAccounts
        GeneralLedgerAccounts: {
          type: CrudGeneralLedgerAccounts,
          defaultValue: [],
          props: {
            caption: 'common.emptyString',
            openInFullscreen: true,
            parentIdFilterField: 'domainID',
            permissionCode: 'Domain'
          }
        }
      }
    ]
  };
}

const layoutTabGeneral = {
  named: true,
  sections: [
    {
      width: 50,
      fields: [
        'name',
        'description',
        'domainType',
        'copyLinksFromDomainID',
        'companyID',
        'companyAddress',
        'phone',
        'companyEmail'
      ]
    },
    {
      width: 50,
      fields: ['mailingAddress']
    }
  ]
};
const layoutFullWidth = {
  sections: [
    {
      width: 100
    }
  ]
};

export function getCreateLayoutDefinition(vue) {
  return {
    groups: [layoutTabGeneral, layoutFullWidth]
  };
}
export function getEditLayoutDefinition(vue) {
  return {
    groups: [
      layoutTabGeneral,
      layoutFullWidth,
      layoutFullWidth,
      layoutFullWidth,
      layoutFullWidth,
      layoutFullWidth,
      layoutFullWidth,
      layoutFullWidth,
      layoutFullWidth,
      layoutFullWidth,
      layoutFullWidth
    ]
  };
}

export function createItemFromFormData(vue, form) {
  return form;
}

export function createFormDataFromItem(vue, item) {
  return item;
}
